import {
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { NotificationsTab_brand$data } from "../__generated__/NotificationsTab_brand.graphql";
import { DialogType } from "../NotificationsTab";
import NotificationTableRow from "./NotificationTableRow";

type NotificationData = NotificationsTab_brand$data["notifications"];

type NotificationsTableProps = {
  notifications: NotificationData;
  title: string;
  isLoadingNext: boolean;
  hasNext: boolean;
  onLoadNext: (count: number) => void;
  onOpenDialog: (dialogType: DialogType, notificationId: string) => void;
  justUpdatedNotifID: string | null;
} & Pick<
  React.ComponentProps<typeof NotificationTableRow>,
  "hidePastPublished"
>;

const NotificationsTable = ({
  notifications,
  title,
  isLoadingNext,
  hasNext,
  onLoadNext,
  onOpenDialog,
  justUpdatedNotifID,
  hidePastPublished,
}: NotificationsTableProps) => {
  // Table Controls
  const [tablePage, setTablePage] = useState(0);
  const [tableRowsPerPage, setTableRowsPerPage] = useState(10);

  const handleChangePage = (event: unknown, newPage: number) => {
    if (newPage > tablePage) {
      onLoadNext(tableRowsPerPage);
    }
    setTablePage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const difference = +event.target.value - tableRowsPerPage;
    if (difference > 0) {
      onLoadNext(difference);
    }
    setTableRowsPerPage(+event.target.value);
    setTablePage(0);
  };

  return (
    <Paper sx={{ width: "100%", mb: 2 }}>
      <TableContainer>
        <Table
          sx={{ minWidth: 650, tableLayout: "fixed" }}
          aria-label="simple table"
          size="small"
        >
          <TableHead
            sx={{
              background: "#F3F4F6",
            }}
          >
            <TableRow>
              <TableCell width="35%">
                <Typography variant="overline">{title}</Typography>
              </TableCell>
              <TableCell align="center">
                <Typography variant="overline">Audience</Typography>
              </TableCell>
              <TableCell align="center">
                <Typography variant="overline">Campaign</Typography>
              </TableCell>
              <TableCell align="center" width={"15%"}>
                <Typography variant="overline">Status</Typography>
              </TableCell>
              <TableCell align="center">
                <Typography variant="overline" whiteSpace={"nowrap"}>
                  Publish Time
                </Typography>
              </TableCell>
              <TableCell align="center" width="200px">
                <Typography variant="overline">Actions</Typography>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {notifications.edges
              .slice(
                tablePage * tableRowsPerPage,
                tablePage * tableRowsPerPage + tableRowsPerPage
              )
              .map((notificationNode) => (
                <NotificationTableRow
                  key={notificationNode.node.id}
                  isNew={justUpdatedNotifID === notificationNode.node.id}
                  notification={notificationNode.node}
                  onOpenDialog={(dialogType: DialogType) => {
                    onOpenDialog(dialogType, notificationNode.node.id);
                  }}
                  hidePastPublished={hidePastPublished}
                />
              ))}
            {isLoadingNext && hasNext
              ? notifications.edges.length <
                  (tablePage + 1) * tableRowsPerPage && (
                  <TableCell
                    colSpan={6}
                    align="center"
                    sx={{
                      padding: "24px 0",
                    }}
                  >
                    <CircularProgress color="primary" size={32} />
                  </TableCell>
                )
              : null}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[10, 25]}
        component="div"
        count={notifications.totalCount ?? notifications.edges.length}
        rowsPerPage={tableRowsPerPage}
        page={tablePage}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        sx={{
          display:
            (notifications.totalCount ?? 0) <= tableRowsPerPage
              ? "none"
              : "block",
        }}
      />
    </Paper>
  );
};

export default NotificationsTable;
